import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { 
  ShoppingBag, 
  User, 
  Search, 
  Menu, 
  X,
  Heart,
  MapPin,
  Phone,
  LogOut,
  Package,
  ChevronDown
} from 'lucide-react';
import { useCartStore } from '../store/cartStore';
import { useAuthStore } from '../store/authStore';
import { SearchBar } from './SearchBar';
import { CategoryMenu } from './CategoryMenu';
import toast from 'react-hot-toast';

export const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const { itemCount } = useCartStore();
  const { isAuthenticated, user, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const userDropdownRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
  const toggleSearch = () => setIsSearchOpen(!isSearchOpen);
  const toggleUserDropdown = () => setIsUserDropdownOpen(!isUserDropdownOpen);

  const handleLogout = () => {
    clearAuth();
    setIsUserDropdownOpen(false);
    toast.success('Logged out successfully');
    navigate('/');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userDropdownRef.current && !userDropdownRef.current.contains(event.target as Node)) {
        setIsUserDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <>
      {/* Top Bar */}
      <div className="bg-charcoal-900 text-white py-3">
        <div className="container">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center space-x-6">
              <div className="flex items-center space-x-2">
                <Phone className="h-4 w-4 text-primary-400" />
                <span className="font-medium">+91 9876543210</span>
              </div>
              <div className="flex items-center space-x-2">
                <MapPin className="h-4 w-4 text-primary-400" />
                <span className="font-medium">Free shipping on orders above ₹999</span>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-6">
              <Link to="/about" className="hover:text-primary-400 transition-colors font-medium">
                About Us
              </Link>
              <Link to="/contact" className="hover:text-primary-400 transition-colors font-medium">
                Contact
              </Link>
              {isAuthenticated ? (
                <Link to="/profile" className="hover:text-primary-400 transition-colors font-medium">
                  Welcome, {user?.firstName || user?.email}
                </Link>
              ) : (
                <Link to="/login" className="hover:text-primary-400 transition-colors font-medium">
                  Login
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Header */}
      <header className="bg-white shadow-lg sticky top-0 z-50 border-b border-charcoal-100">
        <div className="container">
          <div className="flex items-center h-24">
            {/* Logo */}
            <div className="flex-shrink-0 mr-12">
              <Link to="/" className="group">
                <img
                  src="/logo.png"
                  alt="NINEEYE"
                  className="h-24 w-auto group-hover:scale-105 transition-transform duration-300"
                  onError={(e) => {
                    // Fallback to a simple text logo if image fails to load
                    (e.target as HTMLImageElement).style.display = 'none';
                    const fallback = document.createElement('div');
                    fallback.className = 'text-4xl font-bold text-primary-600';
                    fallback.textContent = 'NINEEYE';
                    (e.target as HTMLImageElement).parentNode?.appendChild(fallback);
                  }}
                />
              </Link>
            </div>

            {/* Desktop Navigation - Centered */}
            <nav className="hidden lg:flex items-center space-x-10 flex-1 justify-center">
              <Link 
                to="/" 
                className="text-charcoal-700 hover:text-primary-600 transition-colors font-semibold text-sm uppercase tracking-wide"
              >
                Home
              </Link>
              <CategoryMenu />
              <Link 
                to="/shop" 
                className="text-charcoal-700 hover:text-primary-600 transition-colors font-semibold text-sm uppercase tracking-wide"
              >
                Shop
              </Link>
              <Link 
                to="/about" 
                className="text-charcoal-700 hover:text-primary-600 transition-colors font-semibold text-sm uppercase tracking-wide"
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="text-charcoal-700 hover:text-primary-600 transition-colors font-semibold text-sm uppercase tracking-wide"
              >
                Contact
              </Link>
            </nav>

            {/* Search Bar - Desktop */}
            <div className="hidden md:flex flex-1 max-w-md ml-8">
              <SearchBar />
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-4">
              {/* Search Button - Mobile */}
              <button
                onClick={toggleSearch}
                className="md:hidden p-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                <Search className="h-5 w-5" />
              </button>

              {/* Wishlist */}
              <button className="p-2 text-gray-600 hover:text-primary-600 transition-colors relative">
                <Heart className="h-5 w-5" />
                <span className="absolute -top-1 -right-1 bg-accent-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  0
                </span>
              </button>

              {/* User Account */}
              {isAuthenticated ? (
                <div className="relative" ref={userDropdownRef}>
                  <button 
                    onClick={toggleUserDropdown}
                    className="flex items-center space-x-1 p-2 text-gray-600 hover:text-primary-600 transition-colors"
                  >
                    <User className="h-5 w-5" />
                    <ChevronDown className="h-3 w-3" />
                  </button>
                  
                  {/* User Dropdown */}
                  {isUserDropdownOpen && (
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50">
                      <div className="px-4 py-2 border-b border-gray-100">
                        <p className="text-sm font-medium text-gray-900">
                          {user?.firstName || user?.email}
                        </p>
                        <p className="text-xs text-gray-500">{user?.email}</p>
                      </div>
                      
                      <Link
                        to="/profile"
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <User className="h-4 w-4" />
                        <span>Profile</span>
                      </Link>
                      
                      <Link
                        to="/orders"
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setIsUserDropdownOpen(false)}
                      >
                        <Package className="h-4 w-4" />
                        <span>My Orders</span>
                      </Link>
                      
                      <button
                        onClick={handleLogout}
                        className="flex items-center space-x-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors w-full text-left"
                      >
                        <LogOut className="h-4 w-4" />
                        <span>Logout</span>
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <button 
                  onClick={() => navigate('/login')}
                  className="p-2 text-gray-600 hover:text-primary-600 transition-colors"
                >
                  <User className="h-5 w-5" />
                </button>
              )}

              {/* Cart */}
              <button 
                onClick={() => navigate('/cart')}
                className="p-2 text-gray-600 hover:text-primary-600 transition-colors relative"
              >
                <ShoppingBag className="h-5 w-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-primary-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {itemCount}
                  </span>
                )}
              </button>

              {/* Mobile Menu Button */}
              <button
                onClick={toggleMenu}
                className="lg:hidden p-2 text-gray-600 hover:text-primary-600 transition-colors"
              >
                {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Search Bar */}
        {isSearchOpen && (
          <div className="md:hidden border-t border-gray-200 p-4">
            <SearchBar />
          </div>
        )}

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white">
            <nav className="container py-4 space-y-4">
              <Link 
                to="/" 
                className="block text-gray-700 hover:text-primary-600 transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Home
              </Link>
              <Link 
                to="/shop" 
                className="block text-gray-700 hover:text-primary-600 transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Shop
              </Link>
              <Link 
                to="/about" 
                className="block text-gray-700 hover:text-primary-600 transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                About
              </Link>
              <Link 
                to="/contact" 
                className="block text-gray-700 hover:text-primary-600 transition-colors font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                Contact
              </Link>
              {isAuthenticated && (
                <>
                  <Link 
                    to="/profile" 
                    className="block text-gray-700 hover:text-primary-600 transition-colors font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link 
                    to="/orders" 
                    className="block text-gray-700 hover:text-primary-600 transition-colors font-medium"
                    onClick={() => setIsMenuOpen(false)}
                  >
                    My Orders
                  </Link>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMenuOpen(false);
                    }}
                    className="block w-full text-left text-red-600 hover:text-red-700 transition-colors font-medium"
                  >
                    Logout
                  </button>
                </>
              )}
            </nav>
          </div>
        )}
      </header>
    </>
  );
};
